'use client'

import { OTHER_COUNTRY } from '@/constants/constants'
import { getCountryList } from '@/layouts/cjr-layout/actions'
import { Country } from '@/types/types'
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'

interface CountryContextType {
  countryList: Country[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

const CountryContext = createContext<CountryContextType | undefined>(undefined)

export const useCountryContext = () => {
  const context = useContext(CountryContext)
  if (context === undefined) {
    throw new Error('useCountryContext must be used within a CountryProvider')
  }
  return context
}

interface CountryProviderProps {
  children: ReactNode
}

export const CountryProvider: React.FC<CountryProviderProps> = ({ children }) => {
  const [countryList, setCountryList] = useState<Country[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCountries = async () => {
    // Check if already fetched (prevents duplicate calls)
    if (countryList.length > 0) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      const res = await getCountryList()
      setCountryList([...res.data, OTHER_COUNTRY] as Country[])
    } catch (err) {
      console.error('Error fetching countries:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch countries')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCountries()
  }, []) // Empty dependency array ensures this only runs once

  const value = {
    countryList,
    loading,
    error,
    refetch: fetchCountries,
  }

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
}